import { tokens, Text, Button, Link, Stack, IconButton } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useStorage } from '@hotjar/insights-common/hooks/useStorage';
import { useTranslation } from 'react-i18next';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { toPlansAndBillingWithContext } from '@hotjar/webapp/features/Billing/ContextualBanner/utils';
import { PBReferrers } from '@hotjar/webapp/features/Billing/ContextualBanner/types';
import { useCurrentSite } from '@hotjar/site';
import type { ResponseLimitLocation } from '../ResponseLimitAlert/ResponseLimitAlert';
import heroImageSrc from './person-sitting-on-time.svg';

const Border = styled.article`
  border: 1px solid ${tokens.ALIAS_COLORS.ui_border};
  border-radius: ${tokens.radius.m};
  padding: ${tokens.spacer.x2};
  margin: 0 30px 30px 30px;
  padding: ${tokens.spacer.x2};
  justify-content: space-between;
  max-width: 1039px;
`;

const Header = styled(Stack)`
  justify-content: space-between;
`;

const Title = styled(Text)`
  display: block;
`;

const Body = styled(Stack)`
  max-width: 64ch;
`;

const Footer = styled(Stack)`
  align-items: center;
`;

const HeroBackground = styled.div`
  width: 148px;
  height: 80px;
  padding: ${tokens.spacer.x1};
  background: ${tokens.BRAND_COLORS.noon_100};
  margin-top: ${tokens.spacer.x1};
  flex-shrink: 0;
  display: flex;
  justify-content: center;
`;

const HeroImage = styled.img`
  max-width: 100%;
  max-height: 100%;
`;

const LearnMoreLink = styled(Link)`
  text-decoration: none;
`;

const Content = styled(Stack)`
  justify-content: space-between;
  margin-top: ${tokens.spacer.x0pt5};
`;

export const AskPricingChangeAlert = ({
  location,
}: {
  location: ResponseLimitLocation;
}): JSX.Element | null => {
  const { click: trackClick } = useTrack();
  const { data: currentSite } = useCurrentSite();
  const { t } = useTranslation('feedback');

  const [isDismissed, setIsDissmissed] = useStorage<boolean>(
    'localStorage',
    'ask_price_change_alert_dismissed',
    false,
  );

  const onUpgrade = () => {
    trackClick('Upgrade Ask grace period', location, {});
    toPlansAndBillingWithContext({
      referrer: PBReferrers.ASK_GRACE_PERIOD,
    });
  };

  if (isDismissed || currentSite?.effectiveAskPlan !== 'basic') return null;

  return (
    <Border role="alert">
      <Header>
        <Title textType="subtitle1">{t('ask-pricing-change.title')}</Title>
        <IconButton
          kind="ghost"
          size="s"
          as="button"
          description={t('ask-pricing-change.dismiss')}
          tooltipText={t('ask-pricing-change.dismiss')}
          icon="close"
          onClick={() => {
            setIsDissmissed(true);
          }}
        />
      </Header>
      <Content>
        <Body direction="column" spacing="x3">
          <Text textType="body2" textColor="text_light">
            {t('ask-pricing-change.info')}
          </Text>
          <Footer spacing="x2">
            <Button size="s" onClick={onUpgrade}>
              {t('ask-pricing-change.cta')}
            </Button>
            <LearnMoreLink
              kind="standalone"
              as="a"
              isExternal
              href="https://www.hotjar.com/blog/updating-our-pricing-model/"
            >
              {t('ask-pricing-change.learn-more')}
            </LearnMoreLink>
          </Footer>
        </Body>
        <HeroBackground>
          <HeroImage alt="" src={heroImageSrc} />
        </HeroBackground>
      </Content>
    </Border>
  );
};
